import { Box, Tooltip } from "@mui/material";
import React, { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import CustomButton from "../components/common/CustomButton";
import CustomLabel from "../components/common/CustomLabel";
import CustomTextField from "../components/common/CustomTextField";
import { setUserPassword } from "../services/SetPasswordService";
import ToastNotification from "../components/ToastNotification";
import {
  GlobalReset,
  OuterContainer,
  Container,
  LeftPanel,
  RightPanel,
} from "../components/layout/CommonLoginLayout";
const passwordRules = [
  {
    label: "At least 8 characters",
    test: (pw: string) => pw.length >= 8,
  },
  {
    label: "At least one uppercase letter",
    test: (pw: string) => /[A-Z]/.test(pw),
  },
  {
    label: "At least one lowercase letter",
    test: (pw: string) => /[a-z]/.test(pw),
  },
  {
    label: "At least one symbol",
    test: (pw: string) => /[^A-Za-z0-9]/.test(pw),
  },
];
const SetPassword = () => {
  const [formData, setFormData] = useState({
    email: "",
    code: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [error, setError] = useState<{
    email?: string;
    code?: string;
    newPassword?: string;
    confirmPassword?: string;
    general?: string;
  }>({});

  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [passwordFocused, setPasswordFocused] = useState(false);

  const navigate = useNavigate();

  const validateField = (name: string, value: string) => {
    let errorMessage = "";

    if (name === "email") {
      if (!value.trim()) {
        errorMessage = "Email is required.";
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
        errorMessage = "Invalid email address.";
      }
    }

    if (name === "code" && !value.trim()) {
      errorMessage = "Code is required.";
    }

    if (name === "newPassword") {
      if (!value) {
        errorMessage = "Password is required.";
      } else {
        const failedRule = passwordRules.find((rule) => !rule.test(value));
        if (failedRule) {
          errorMessage = "Password does not meet all requirements.";
        }
      }
    }

    if (name === "confirmPassword") {
      if (!value) {
        errorMessage = "Please confirm your password.";
      } else if (value !== formData.newPassword) {
        errorMessage = "Passwords do not match.";
      }
    }

    return errorMessage;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));

    const errorMessage = validateField(name, value);
    setError((prevError) => ({ ...prevError, [name]: errorMessage }));
  };

  const handleSetPassword = async () => {
    if (submitting) return;

    const { email, code, newPassword, confirmPassword } = formData;
    const newErrors = {
      email: validateField("email", email),
      code: validateField("code", code),
      newPassword: validateField("newPassword", newPassword),
      confirmPassword: validateField("confirmPassword", confirmPassword),
    };

    if (
      newErrors.email ||
      newErrors.code ||
      newErrors.newPassword ||
      newErrors.confirmPassword
    ) {
      setError(newErrors);
      return;
    }

    setSubmitting(true);
    setError({});
    setSuccessMessage("");

    try {
      await setUserPassword(email, { code, newPassword });
      ToastNotification.success("Password was successfully set.");
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err: any) {
      ToastNotification.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Outlet />
      <GlobalReset />
      <OuterContainer>
        <Container>
          <RightPanel>
            <CustomLabel
              text="SET YOUR PASSWORD"
              fontWeight="bold"
              fontSize="24px"
              color="#A39455"
              sx={{ marginBottom: 3, textAlign: "left", width: "100%" }}
            />

            {error.general && (
              <CustomLabel
                text={error.general}
                fontSize="14px"
                color="red"
                sx={{ marginBottom: 2, textAlign: "center", width: "100%" }}
              />
            )}

            {successMessage && (
              <CustomLabel
                text={successMessage}
                fontSize="14px"
                color="green"
                sx={{ marginBottom: 2, textAlign: "center", width: "100%" }}
              />
            )}

            <CustomTextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              bgColor="#f5f5f5"
              fullWidth
              sx={{ marginBottom: 2 }}
              error={!!error.email}
              helperText={error.email}
              onKeyDown={({ key }) => {
                if (key === "Enter") handleSetPassword();
              }}
            />

            <CustomTextField
              label="Code"
              name="code"
              type="text"
              value={formData.code}
              onChange={handleChange}
              bgColor="#f5f5f5"
              fullWidth
              sx={{ marginBottom: 2 }}
              error={!!error.code}
              helperText={error.code}
              onKeyDown={({ key }) => {
                if (key === "Enter") handleSetPassword();
              }}
            />
            <Tooltip
              title={
                <Box>
                  {passwordRules
                    .filter((rule) => !rule.test(formData.newPassword))
                    .map((rule) => (
                      <Box
                        key={rule.label}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: "red",
                          fontSize: "15px",
                          mb: 0.5,
                        }}
                      >
                        <span style={{ marginRight: 8, fontWeight: "bold" }}>
                          ✗
                        </span>
                        {rule.label}
                      </Box>
                    ))}
                  {passwordRules.every((rule) =>
                    rule.test(formData.newPassword)
                  ) && (
                    <Box
                      sx={{
                        color: "green",
                        fontWeight: "bold",
                        fontSize: "15px",
                      }}
                    >
                      All password requirements met!
                    </Box>
                  )}
                </Box>
              }
              open={passwordFocused}
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#fff",
                    color: "#222",
                    fontSize: "15px",
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 3,
                    maxWidth: 260,
                  },
                },
                arrow: {
                  sx: {
                    color: "#fff",
                  },
                },
              }}
            >
              <CustomTextField
                label="New Password"
                name="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={handleChange}
                bgColor="#f5f5f5"
                fullWidth
                sx={{ marginBottom: 2 }}
                error={!!error.newPassword}
                helperText={error.newPassword}
                onKeyDown={({ key }) => {
                  if (key === "Enter") handleSetPassword();
                }}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
              />
            </Tooltip>

            <CustomTextField
              label="Confirm New Password"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              bgColor="#f5f5f5"
              fullWidth
              sx={{ marginBottom: 3 }}
              error={!!error.confirmPassword}
              helperText={error.confirmPassword}
              onKeyDown={({ key }) => {
                if (key === "Enter") handleSetPassword();
              }}
            />

            <CustomButton
              label="Set Password"
              onClick={handleSetPassword}
              bgColor="#A39455"
              textColor="#fff"
              borderRadius="4px"
              size="medium"
              sx={{
                marginTop: 8,
                alignSelf: "center",
              }}
              disabled={submitting}
            />
          </RightPanel>

          <LeftPanel>
            <Box
              component="img"
              src="/SPARK LOGO-white.png"
              alt="Logo"
              sx={{
                width: "300px",
                height: "auto",
                marginBottom: "40px",
              }}
            />

            <CustomLabel
              text="Please enter the code you received, your email, and set your new password."
              fontSize="14px"
              color="#fff"
              sx={{
                maxWidth: 300,
                lineHeight: 1.6,
                textAlign: "center",
              }}
            />

            <CustomLabel
              text="Powered by Teways"
              fontSize="12px"
              color="#fff"
              fontWeight="bold"
            />
          </LeftPanel>
        </Container>
      </OuterContainer>
    </>
  );
};

export default SetPassword;
